<template>
    <div>
        <section class="product-details">
            <aside>
                <DCard
                    :title="__('products.products.show.details')"
                    padding
                >
                    <p class="text-right mb-0">
                        <Link
                            class="float-right text-decoration-underline text-primary"
                            :href="activityLogUrl"
                        >
                            {{ __('products.products.product_history') }}
                        </Link>
                    </p>
                    <div class="items">
                        <div class="item">
                            <div>{{ __('products.products.form.name') }}</div>
                            <div>
                                {{ product.name }}
                                <DTag
                                    v-if="product.retired === true || product.product_group.retired === true"
                                    color="primary"
                                    :label="__('products.products.form.retired')"
                                />
                            </div>
                        </div>
                        <div class="item">
                            <div>{{ __('products.products.form.productCode') }}</div>
                            <div>{{ product.name_short }}</div>
                        </div>
                        <div class="item">
                            <div>{{ __('products.products.form.invoice_text') }}</div>
                            <div class="text-space-wrap">
                                {{ product.invoice_text }}
                            </div>
                        </div>
                        <div class="item">
                            <div>{{ __('products.products.form.onehome_configuration') }}</div>
                            <div
                                v-if="product.onehome_configuration"
                                class="code-textarea-container"
                            >
                                <DFormTextarea
                                    id="onehome_configuration"
                                    :model-value="formattedOnehomeConfiguration"
                                    rows="10"
                                    :disabled="true"
                                />
                            </div>
                            <div v-else>
                                {{ __('messages.not_set') }}
                            </div>
                        </div>
                        <div class="item">
                            <div>{{ __('products.products.form.productAddonGroup') }}</div>
                            <Link :href="$route('products.product-addon-groups.edit', { product_group: product.product_group.id })">
                                {{ product.product_group.name }}
                            </Link>
                        </div>
                        <div class="item">
                            <div>{{ __('products.products.form.accountingProduct') }}</div>
                            <div>{{ product.accounting_product.name }}</div>
                        </div>
                        <div class="item">
                            <div>{{ __('products.products.form.intercompany_accounting_product') }}</div>
                            <div>{{ product.intercompany_accounting_product?.name }}</div>
                        </div>
                        <div
                            v-if="product.accounting_information"
                            class="item"
                        >
                            <div>{{ __('products.products.form.accounting_information') }}</div>
                            <div>{{ product.accounting_information }}</div>
                        </div>
                        <div class="item">
                            <div>{{ __('products.products.form.fulfillmentStrategy') }}</div>
                            <div>{{ __(`products.fulfillmentStrategies.values.${product.fulfillment_strategy_id}`) }}</div>
                        </div>
                        <div
                            v-if="isInvoiceBeforeRenewalEnabled === false"
                            class="item"
                        >
                            <div>{{ __('products.products.form.lead_time') }}</div>
                            <div>{{ product.lead_time }}</div>
                        </div>
                        <div class="item">
                            <PossibleProductChanges
                                :possible-changes="product.upgrade_products"
                                :title="__('products.products.form.upgrades')"
                            />
                        </div>
                        <div class="item">
                            <PossibleProductChanges
                                :possible-changes="product.downgrade_products"
                                :title="__('products.products.form.downgrades')"
                            />
                        </div>
                        <div class="item">
                            <div>{{ __('products.products.form.dnsTemplate') }}</div>
                            <div v-if="product.dns_template_id !== null">
                                <Link :href="$route('products.dns-templates.show', {dns_template: product.dns_template_id})">
                                    {{ product.dns_template.name }}
                                </Link>
                            </div>
                            <div v-else>
                                {{ __('messages.not_set') }}
                            </div>
                        </div>
                        <div class="item">
                            <div>{{ __('products.products.form.invoice_behaviour') }}</div>
                            <div>
                                <DTag
                                    :color="product.invoice_behaviour === 'exclude' ? 'secondary' : 'success'"
                                    :label="__(`products.invoice_behaviours.${product.invoice_behaviour}`)"
                                />
                            </div>
                        </div>
                        <div class="item">
                            <div>{{ __('products.products.form.disable_invoice_before_renewal.label') }}</div>
                            <div>
                                <DTag
                                    :color="product.disable_invoice_before_renewal === true ? 'secondary' : 'success'"
                                    :label="product.disable_invoice_before_renewal === true ? __('messages.yes') : __('messages.no')"
                                />
                            </div>
                        </div>
                        <div class="item">
                            <div>{{ __('products.products.form.align_billing_period_with_parent.label') }}</div>
                            <div>
                                <DTag
                                    :color="product.align_billing_period_with_parent === true ? 'success' : 'secondary'"
                                    :label="product.align_billing_period_with_parent === true ? __('messages.yes') : __('messages.no')"
                                />
                            </div>
                        </div>
                        <div class="item">
                            <div>{{ __('products.products.form.billing_type.label') }}</div>
                            <div>
                                <DTag
                                    :color="product.billing_type ? 'success' : 'secondary'"
                                    :label="product.billing_type ? __(`customers.billing_types.${product.billing_type}`) : __('messages.not_set')"
                                />
                            </div>
                        </div>
                        <div class="item">
                            <div>{{ __('products.products.form.excluded_customer_protection_law') }}</div>
                            <div>
                                <DTag
                                    :color="product.excluded_customer_protection_law === true ? 'secondary' : 'success'"
                                    :label="product.excluded_customer_protection_law === true ? __('messages.yes') : __('messages.no')"
                                />
                            </div>
                        </div>
                        <div class="item">
                            <div>{{ __('products.products.form.termination_on_dunning') }}</div>
                            <div>
                                <DTag
                                    :color="product.termination_on_dunning === true ? 'secondary' : 'success'"
                                    :label="product.termination_on_dunning === true ? __('messages.yes') : __('messages.no')"
                                />
                            </div>
                        </div>
                        <div class="item">
                            <div>{{ __('products.products.form.winback_ticket_on_termination.label') }}</div>
                            <div>
                                <DTag
                                    :color="product.winback_ticket_on_termination === true ? 'secondary' : 'success'"
                                    :label="product.winback_ticket_on_termination === true ? __('messages.yes') : __('messages.no')"
                                />
                            </div>
                        </div>
                        <div class="item">
                            <div>{{ __('products.products.form.manual_termination_disallowed.label') }}</div>
                            <div>
                                <DTag
                                    :color="product.manual_termination_disallowed === true ? 'secondary' : 'success'"
                                    :label="product.manual_termination_disallowed === true ? __('messages.yes') : __('messages.no')"
                                />
                            </div>
                        </div>
                        <div class="item">
                            <div>{{ __('products.products.form.headline.product_classes') }}</div>
                            <div>
                                {{ product.productClasses ?? __('messages.not_set') }}
                            </div>
                        </div>
                        <div class="item">
                            <div>{{ __('products.products.form.headline.addon_order_limit') }}</div>
                            <div>
                                {{ product.order_limit ?? __('messages.not_set') }}
                            </div>
                        </div>
                    </div>
                </DCard>

                <div>
                    <DCard
                        :title="__('products.products.show.fulfillmentFeatures.title')"
                        padding
                    >
                        <div class="items two-columns">
                            <div
                                class="item"
                            >
                                <div>{{ __('products.products.show.fulfillmentModule') }}</div>
                                <div v-if="product.fulfillment_module">
                                    {{ product.fulfillment_module }}
                                </div>
                                <div v-else>
                                    {{ __('messages.not_set') }}
                                </div>
                            </div>
                            <div
                                v-if="fulfillmentGroupName"
                                class="item"
                            >
                                <div>{{ __(`products.products.show.fulfillmentFeatures.groupName`) }}</div>
                                <Link :href="$route('products.accounts.index', {fulfillmentGroups: [product.fulfillment_account_group_id]})">
                                    {{ fulfillmentGroupName }}
                                </Link>
                            </div>
                            <div
                                v-for="(value, key) in fulfillmentFeatures"
                                :key="key"
                                class="item"
                            >
                                <div>{{ __(`products.products.show.fulfillmentFeatures.${key}`) }}</div>
                                <div>
                                    <DTag
                                        :color="value ? 'success' : 'secondary'"
                                        :label="value ? __('products.products.show.fulfillmentFeatures.true') : __('products.products.show.fulfillmentFeatures.false')"
                                    />
                                </div>
                            </div>
                        </div>
                    </DCard>

                    <DCard
                        :title="__('products.products.show.terminationRules.title')"
                        padding
                    >
                        <div class="items">
                            <div class="item">
                                <div>{{ __('products.products.show.terminationRules.ruleset') }}</div>
                                <div>
                                    {{ product.termination_rule.name }}
                                </div>
                            </div>
                            <div class="item">
                                <div>{{ __('products.products.show.terminationRules.notification.title') }}</div>
                                <div>
                                    {{ getInterval(terminationRules.notificationIntervalForHumans, 'notification') }}
                                </div>
                            </div>
                            <div class="item">
                                <div>{{ __('products.products.show.terminationRules.suspension.title') }}</div>
                                <div>
                                    {{ getInterval(terminationRules.suspensionIntervalForHumans, 'suspension') }}
                                </div>
                            </div>
                            <div class="item">
                                <div>{{ __('products.products.show.terminationRules.deletion.title') }}</div>
                                <div>
                                    {{ getInterval(terminationRules.deletionIntervalForHumans, 'deletion') }}
                                </div>
                            </div>
                        </div>
                    </DCard>

                    <DCard
                        :title="__('products.products.form.config')"
                        padding
                    >
                        <div class="config-values">
                            <ConfigOption
                                v-for="(configOption) in product.config"
                                :key="configOption.name"
                                :config-option="configOption"
                            />
                        </div>
                    </DCard>
                </div>
            </aside>

            <section>
                <DCard
                    :title="__('products.products.show.plans')"
                    :main-title-has-slot="true"
                >
                    <template #titleHasSlot>
                        <DButton
                            v-if="userPermissions.includes('product.view')"
                            id="plans-advanced-view"
                            class="ms-auto"
                            icon="eye"
                            :label="__('products.products.show.plans_advanced_view')"
                            :href="$route('products.products.plans-advanced-view', { product: product.id })"
                        />
                    </template>
                    <div class="table-responsive">
                        <table class="table">
                            <thead>
                                <tr>
                                    <th class="col-3" />
                                    <th>{{ __('products.productPrices.form.valid_from') }}</th>
                                    <th>{{ __('products.productPrices.form.price') }}</th>
                                    <th>{{ __('products.productPrices.form.third_party_cost') }}</th>
                                    <th>{{ __('products.productPrices.form.billing_cycle') }}</th>
                                    <th>{{ __('products.productPrices.form.setup_fee') }}</th>
                                    <th>{{ __('products.productPrices.form.contract_term') }}</th>
                                    <th>{{ __('products.productPrices.form.discount') }}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody
                                v-for="(item, index) in product.plans"
                                :key="index"
                            >
                                <tr class="trGroupNameBackgroundColor">
                                    <td
                                        class="col-20-percent"
                                        colspan="8"
                                    >
                                        <strong>
                                            {{ item.name }} / {{ item.market_region_name }} - {{ __('products.productPlans.runtime_settings.' + item.runtime, { runtime_in_month: item.runtime_in_month }) }}
                                        </strong>
                                    </td>
                                    <td>
                                        <div class="display: flex; grid-gap: 10px;">
                                            <DButton
                                                v-if="userPermissions.includes('product.create') && item.can_add_new_prices"
                                                :id="`product-plans-` + (index + 1) + `-prices-add`"
                                                mini
                                                icon="plus"
                                                @click="$inertia.get(addNewProductPriceLink(item.id))"
                                            />
                                            <DButton
                                                v-if="userPermissions.includes('product.edit')"
                                                :id="`product-plans-` + (index + 1) + `-edit`"
                                                mini
                                                icon="pencil"
                                                @click="$inertia.get(editProductPlan(item.id))"
                                            />
                                            <DButton
                                                v-if="userPermissions.includes('product.delete')"
                                                :id="`product-plans-` + (index + 1) + `-delete`"
                                                mini
                                                icon="trash-can-outline"
                                                color="danger"
                                                :disabled="item.prices.length !== 0 || isInLoadingState"
                                                @click="deleteProductPlan(item.id)"
                                            />
                                        </div>
                                    </td>
                                </tr>
                                <tr
                                    v-for="(price, indexPrices) in item.prices"
                                    :key="indexPrices"
                                >
                                    <td class="col-20-percent" />
                                    <td>
                                        {{ price.valid_from_month }}
                                    </td>
                                    <td>
                                        {{ price.price }}
                                        <template v-if="price.gross_price">
                                            / <br>{{ price.gross_price }}
                                        </template>
                                    </td>
                                    <td>
                                        {{ price.third_party_cost }}
                                    </td>
                                    <td>
                                        {{ price.billing_cycle }}
                                    </td>
                                    <td v-if="price.setup_fee !== null">
                                        {{ price.setup_fee }}
                                        <template v-if="price.gross_setup_fee">
                                            / <br>{{ price.gross_setup_fee }}
                                        </template>
                                    </td>
                                    <td v-else />
                                    <td>
                                        {{ price.contract_term }} {{ price.contract_term_unit }}
                                    </td>
                                    <td v-if="price.discount_value !== null">
                                        {{ price.discount_value }}
                                        {{ price.discount_type === 'Percentage' ? '%' : item.currency }}
                                        <template v-if="price.gross_discount_value">
                                            / <br>{{ price.gross_discount_value +' '+ item.currency }}
                                        </template>
                                        {{ price.discount_name ? ' - ' + price.discount_name : '' }}
                                    </td>
                                    <td v-else />
                                    <td>
                                        <div style="display: flex; grid-gap: 10px;">
                                            <DButton
                                                v-if="userPermissions.includes('product.edit')"
                                                :id="`product-plans-` + (index + 1) + `-products-` + (indexPrices + 1) + `-edit`"
                                                icon="pencil"
                                                mini
                                                @click="$inertia.get(editProductPrice(price.id, item.id))"
                                            />
                                            <DButton
                                                v-if="userPermissions.includes('product.delete')"
                                                :id="`product-plans-` + (index + 1) + `-products-` + (indexPrices + 1) + `-delete`"
                                                icon="trash-can-outline"
                                                color="danger"
                                                mini
                                                :disabled="isInLoadingState"
                                                @click="deleteProductPrice(price.id, item.id)"
                                            />
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </DCard>
            </section>
        </section>

        <DFabs>
            <DFab
                v-if="userPermissions.includes('product.edit')"
                id="btn-edit"
                icon="pencil"
                :label="__('messages.actions.edit')"
                @click="$inertia.get($route('products.product-addons.edit', product.id))"
            />
            <DFab
                v-if="userPermissions.includes('product.create')"
                id="btn-add-product-plan"
                icon="plus"
                color="secondary"
                :label="__('products.products.show.addProductPlanButton')"
                @click="$inertia.get(addNewProductPlanLink)"
            />
            <DFab
                v-if="userPermissions.includes('product.delete')"
                id="btn-add-product-plan"
                :disabled="product.has_contracts === true || isInLoadingState"
                icon="trash-can-outline"
                color="danger"
                :label="__('products.products.show.deleteProductAddonButton')"
                @click="deleteProduct(product.id)"
            />
        </DFabs>

        <DModal
            v-model:show="showConfirmationModal"
            :title="deleteTitle"
            :message="deleteMessage"
            is-confirm
            is-error
            @close="showConfirmationModal = false"
            @confirm="deleteCallBack"
        />

        <DModal
            v-model="activity_log_comment"
            :show="showDeleteProductConfirmationModal || modalHasErrors"
            :title="__('products.products.index.deleteProductAddonConfirmation.title')"
            :message="__('products.products.index.deleteProductAddonConfirmation.message')"
            is-confirm
            is-error
            :is-awaiting-validation="true"
            :show-change-reason-input="true"
            @close="showConfirmationModal = false"
            @confirm="deleteCallBack"
        />
    </div>
</template>

<script>
import ConfigOption from '~@/Components/Customers/Contracts/ConfigOption'
import PossibleProductChanges from '~@/Components/Products/Products/PossibleProductChanges.vue'

export default {
    name: 'ProductAddonsShow',
    components: {
        ConfigOption,
        PossibleProductChanges
    },
    props: {
        product: {
            type: Object,
            default () {
                return {}
            }
        },
        fulfillmentGroupName: {
            type: String,
            default: ''
        },
        fulfillmentFeatures: {
            type: Object,
            default () {
                return {}
            }
        },
        terminationRules: {
            type: Object,
            default () {
                return {}
            }
        },
        isInvoiceBeforeRenewalEnabled: {
            type: Boolean,
            default: () => false
        }
    },
    data () {
        return {
            showConfirmationModal: false,
            showDeleteProductConfirmationModal: false,
            deleteCallBack: () => { return {} },
            deleteTitle: '',
            deleteMessage: '',
            activity_log_comment: null,
            modalHasErrors: false
        }
    },
    computed: {
        addNewProductPlanLink () {
            return this.$route('products.product-addons.products.plans.create', { product: this.product.id })
        },
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        activityLogUrl () {
            return this.$route('tools.activity-logs.index', {
                subjectType: 'Product',
                subjectId: this.product.id
            })
        },
        formattedOnehomeConfiguration () {
            try {
                return JSON.stringify(JSON.parse(this.product.onehome_configuration), null, 2)
            } catch {
                return this.product.onehome_configuration
            }
        }
    },
    methods: {
        editProductPrice (id, planId) {
            return this.$route('products.product-addons.products.plans.prices.edit', {
                product: this.product.id,
                plan: planId,
                productPrice: id
            })
        },
        editProductPlan (id) {
            return this.$route('products.product-addons.products.plans.edit', {
                product: this.product.id,
                plan: id
            })
        },
        deleteProductPlan (id) {
            this.deleteTitle = this.__('products.products.show.deleteProductPlanConfirmation.title')
            this.deleteMessage = this.__('products.products.show.deleteProductPlanConfirmation.message')
            this.deleteCallBack = () => {
                this.$inertia.delete(
                    this.$route('products.product-addons.products.plans.destroy', {
                        product: this.product.id,
                        plan: id
                    }),
                    {
                        preserveScroll: true
                    }
                )
                this.showConfirmationModal = false
            }
            this.showConfirmationModal = true
        },
        deleteProductPrice (id, planId) {
            this.deleteTitle = this.__('products.products.show.deleteProductPriceConfirmation.title')
            this.deleteMessage = this.__('products.products.show.deleteProductPriceConfirmation.message')
            this.deleteCallBack = () => {
                this.$inertia.delete(
                    this.$route('products.product-addons.products.plans.prices.destroy', {
                        product: this.product.id,
                        plan: planId,
                        productPrice: id
                    }),
                    {
                        preserveScroll: true
                    }
                )
                this.showConfirmationModal = false
            }
            this.showConfirmationModal = true
        },
        deleteProduct (id) {
            this.deleteTitle = this.__('products.products.index.deleteProductConfirmation.title')
            this.deleteMessage = this.__('products.products.index.deleteProductConfirmation.message')
            this.deleteCallBack = () => {
                this.$inertia.delete(this.$route('products.product-addons.destroy', {
                    product: id,
                    activity_log_comment: this.activity_log_comment
                }), {
                    onSuccess: () => {
                        this.showDeleteProductConfirmationModal = false
                        this.modalHasErrors = false
                    },
                    onError: () => {
                        this.modalHasErrors = true
                    }
                })
            }
            this.showDeleteProductConfirmationModal = true
        },
        addNewProductPriceLink (id) {
            return this.$route('products.product-addons.products.plans.prices.create', {
                product: this.product.id,
                plan: id
            })
        },
        getInterval (interval, type) {
            if (interval === null) {
                return this.__('products.products.show.terminationRules.' + type + '.not_supported')
            }

            const translated = this.__(interval)

            // If interval and translated are the same it means we are dealing with a parsed value and not a translation from BE
            if (interval !== translated) {
                return translated
            }

            const value = (interval.startsWith('-') ? interval.slice(1) : interval) + (interval.startsWith('-') ? ' before' : ' after')

            const key = (type === 'deletion' && this.terminationRules.suspensionIntervalForHumans === null) ? 'description_without_suspension' : 'description'

            return this.__('products.products.show.terminationRules.' + type + '.' + key, { value: value })
        }
    }
}
</script>

<style lang="scss" scoped>
.product-details {
    display: grid;
    // minmax(0, 1fr) keeps the single column from exceeding the container so the
    // wide plans table scrolls inside .table-responsive instead of blowing out the page.
    grid-template-columns: minmax(0, 1fr);
    grid-gap: 15px;

    & aside {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-gap: 15px;
        align-items: flex-start;

        & > div {
            display: grid;
            grid-gap: 15px;
            align-items: flex-start;
        }

        & .items {
            display: grid;
            grid-gap: 15px;
            align-items: flex-start;

            &.two-columns {
                grid-template-columns: 50% 50%;
            }

            & .item {
                display: grid;
                grid-gap: 10px;
                align-items: flex-start;

                & > :deep(div:nth-child(1)) {
                    font-weight: bold;
                }
                & > :deep(div:nth-child(2)) {
                    color: var(--colorTextMuted);
                }
            }
        }

        & .config-values {
            & > div {
                display: grid;
                // See https://stackoverflow.com/a/52861514
                grid-template-columns: 1fr minmax(0, 2fr);
                grid-gap: 15px;
                align-items: center;

                &:deep(span:first-child) {
                    font-weight: bold;
                }

                &:deep(span:nth-child(2)) {
                    line-height: 1.5em;
                    word-break: break-all;
                }
            }
        }
    }
}

.table {
    margin-bottom: 0;
    margin-top: -1px;
}

.table thead th {
    padding: 0.75rem !important;
    font-weight: bold;
    vertical-align: middle !important;
    border-top: 1px solid #d8dbe0;
}

.trGroupNameBackgroundColor {
    line-height: 10px;
}

.col-20-percent {
    width: 20%;
}
.text-space-wrap {
    white-space: pre-wrap;
}
.no-grid-gap {
    grid-gap: 0;
}
</style>
